import React from "react";
import { motion } from "framer-motion";

const Hero: React.FC = () => {
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 60,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 px-4 py-16 relative overflow-hidden">
      {/* Animated Background Gradients */}
      <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 via-purple-600/20 to-blue-600/20 animate-pulse"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-cyan-500/10 to-transparent"></div>

      <motion.div
        className="max-w-6xl mx-auto text-center relative z-10"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        {/* Logo/Brand */}
        <motion.div variants={fadeInUp}>
          <h1 className="text-7xl md:text-8xl font-bold text-white mb-6">
            이노페이
          </h1>
          <div className="w-32 h-2 bg-white mx-auto rounded-full mb-8 shadow-lg"></div>
        </motion.div>

        {/* Main Headline */}
        <motion.div variants={fadeInUp}>
          <h2 className="text-2xl md:text-4xl font-semibold text-white mb-6 leading-relaxed">
            신용카드만 있다면!
            <br />
            <span className="text-blue-200">
              항상 빠른 카드 현금화 서비스를
            </span>{" "}
            제공하는 이노페이입니다!
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            전문 서비스: 신용카드 현금화 | 카드한도 내 현금화 | 업계 최저 수수료
          </p>
        </motion.div>

        {/* Subtitle */}
        <motion.div variants={fadeInUp}>
          <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            365일 24시간 언제 어디서든
            <br />
            신용카드 한도 만큼 현금화 가능
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          variants={fadeInUp}
        >
          <motion.a
            href="tel:010-9733-3651"
            className="bg-gradient-to-r from-gray-100 to-white hover:from-white hover:to-gray-100 text-gray-800 px-8 py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl hover:shadow-white/30 transition-all duration-300 flex items-center gap-3 hover:scale-105"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            유선문의: 010-9733-3651
          </motion.a>
        </motion.div>

        {/* Feature Highlights */}
        <motion.div
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
          variants={fadeInUp}
          whileInView="animate"
          viewport={{ once: false, margin: "-100px" }}
        >
          <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 backdrop-blur-sm border border-blue-400 border-opacity-50 rounded-2xl p-6 shadow-lg hover:shadow-2xl hover:shadow-blue-400/30 transition-all duration-300">
            <div className="w-16 h-16 bg-blue-500 rounded-xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              업계 최저 수수료
            </h3>
            <p className="text-gray-300">
              이노페이는 업계 최저 수수료를 고객님들께 약속합니다!
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-900/30 to-blue-900/30 backdrop-blur-sm border border-green-400 border-opacity-50 rounded-2xl p-6 shadow-lg hover:shadow-2xl hover:shadow-green-400/30 transition-all duration-300">
            <div className="w-16 h-16 bg-green-500 rounded-xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              최대 3분 빠른입금
            </h3>
            <p className="text-gray-300">
              결제후 입금까지 최대 3분안으로 신속 정확한 빠른입금!
            </p>
          </div>

          <div className="bg-gradient-to-br from-yellow-900/30 to-purple-900/30 backdrop-blur-sm border border-yellow-400 border-opacity-50 rounded-2xl p-6 shadow-lg hover:shadow-2xl hover:shadow-yellow-400/30 transition-all duration-300">
            <div className="w-16 h-16 bg-yellow-500 rounded-xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              무이자 할부 지원
            </h3>
            <p className="text-gray-300">무이자할부로 카드값 부담없이!</p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
